import React, { useState, useEffect, useRef } from "react"
import { Link } from "gatsby"
import cx from "classnames"

import flatListToHierarchical from "@utils/flatListToHierarchical"
import { parseLink } from "@utils/urlParser"
import { useSiteTranslations } from "@hooks/useSiteTranslations"
import { isShopDisabled } from "@utils/isShopDisabled"
import CountryLogo from "../../../assets/images/country-picker/gbFlag.jpg"
import { getSrc } from "gatsby-plugin-image"

import * as s from "./Nav.module.scss"

import Arrow from "../../../assets/icons/arrow.inline.svg"
import { MenuFooter } from "../header/MenuFooter"

export const Nav = ({
  mainMenuItems,
  currentPage,
  setShowMenu,
  countryPicker,
  wpPage,
  isOpen,
}) => {
  const [isClient, setClient] = useState(false)
  const [activeHub, setActiveHub] = useState(null)
  const [hoveredHub, setHoveredHub] = useState(null)
  const [mobileSubmenuParent, setMobileSubmenuParent] = useState<any>(null)
  const mobileParentButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const mobileBackButtonRef = useRef<HTMLButtonElement | null>(null)
  const lastOpenedParentTitleRef = useRef<string | null>(null)
  const key = isClient ? "client" : "server"

  // Reset the mobile drill-down whenever the menu closes
  useEffect(() => {
    if (!isOpen) {
      setMobileSubmenuParent(null)
      lastOpenedParentTitleRef.current = null
    }
  }, [isOpen])

  // Move focus when drilling in / back out on mobile
  useEffect(() => {
    if (!isOpen) return
    if (mobileSubmenuParent) {
      setTimeout(() => {
        mobileBackButtonRef.current?.focus()
      }, 50)
    } else if (lastOpenedParentTitleRef.current) {
      const btn = mobileParentButtonRefs.current[lastOpenedParentTitleRef.current]
      setTimeout(() => {
        btn?.focus()
      }, 50)
    }
  }, [mobileSubmenuParent, isOpen])

  useEffect(() => {
    setClient(true)
  }, [])

  const shopDisabledCheck = isShopDisabled()
  const filteredMenuItems = shopDisabledCheck
    ? mainMenuItems.nodes.filter(item => item.title !== "Shop")
    : mainMenuItems.nodes
  const hierarchicalMenu = flatListToHierarchical(filteredMenuItems)
  const {
    headerTranslations: { header },
  } = useSiteTranslations()

  const parentPage = currentPage.page?.ancestors?.nodes[0]

  useEffect(() => {
    if (currentPage.page && !parentPage?.title) {
      setActiveHub(currentPage.page.title)
      return
    }

    if (currentPage.page && parentPage?.title) {
      setActiveHub(parentPage.title)
      return
    }

    if (currentPage.title === "Shop") {
      setActiveHub("Shop")
      return
    }

    if (currentPage.handle) {
      setActiveHub("Shop")
      return
    }

    if (currentPage.category && currentPage.title) {
      setActiveHub("Shop")
      return
    }

    if (currentPage.category) {
      setActiveHub("News")
    }
  }, [currentPage])

  const isCurrentChildPage = (child) => {
    const currentTitle = currentPage.page?.title || currentPage.title
    if (currentTitle && child.title === currentTitle) return true

    const normalise = (u: string) =>
      (u || "")
        .replace(/^https?:\/\/[^/]+/, "")
        .replace(/^\/(gb|uk|es|us|it|zh)(?=\/|$)/, "")
        .replace(/\/$/, "") || "/"

    const currentPath =
      (typeof window !== "undefined" && window.location?.pathname) ||
      currentPage.page?.uri ||
      currentPage.uri ||
      ""

    if (currentPath && child.url) {
      return normalise(currentPath) === normalise(parseLink(child.url))
    }
    return false
  }

  const linkIsActive = (item, menu) => {
    if (menu === "main") {
      return activeHub === item.title
    }

    if (menu === "sub") {
      if (isCurrentChildPage(item)) return true
      if (currentPage.category === "Latest" && item.title === "News") {
        return true
      }
      if (currentPage.category && currentPage.title) {
        return item.title === currentPage.title
      }
      if (currentPage.category) {
        return currentPage.category.slug === item.title
      }
      return false
    }
  }

  if (!isClient) return null

  const submenuOpen = Boolean(mobileSubmenuParent)

  return (
    <>
      <div
        className={cx(s.MenuWrapper, {
          [s.MenuWrapper___submenuOpen]: submenuOpen,
        })}
      >
        <div className={s.MenuWrapper__lhs}>
          <div className={s.MenuNav}>
            <div className={s.Nav} key={key}>
              <div className={`${s.NavMain} ${s.NavMain___hasActive}`}>
                <div>
                  <span className={s.Nav__subTitle}>{header.linksTitle}</span>
                  {/* Desktop list — unchanged flyout behaviour */}
                  <ul className={s.Nav__desktopList}>
                    {hierarchicalMenu.map((item, i) => {
                      const hasChildren = item.children && item.children.length > 0
                      return (
                      <li
                        key={`parent-${i}`}
                        onMouseEnter={() => hasChildren && setHoveredHub(item.title)}
                        onMouseLeave={() => hasChildren && setHoveredHub(null)}
                        onFocus={() => hasChildren && setHoveredHub(item.title)}
                      >
                        <div className={s.Nav__desktop}>
                          <Link
                            to={parseLink(item.url)}
                            className={cx(s.Nav__link, {
                              [s.Nav__link___active]: linkIsActive(item, "main"),
                            })}
                            aria-haspopup="true"
                            aria-expanded={hoveredHub === item.title}
                            onClick={() => setShowMenu(false)}
                            onKeyDown={e => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                setHoveredHub(item.title)
                              }
                            }}
                          >
                            {item.title}
                            <Arrow />
                          </Link>

                          {hasChildren && (
                            <SubMenu
                              title={item.title}
                              links={item.children}
                              activeHub={hoveredHub}
                              setShowMenu={setShowMenu}
                              setActiveHub={setHoveredHub}
                              linkIsActive={linkIsActive}
                            />
                          )}
                        </div>
                      </li>
                      )
                    })}
                  </ul>

                  {/* Mobile drill-down */}
                  <div
                    className={cx(s.MobileNav, {
                      [s.MobileNav___submenuOpen]: submenuOpen,
                    })}
                  >
                    <div
                      className={s.MobileNav__panel}
                      aria-hidden={submenuOpen}
                    >
                      <ul className={s.MobileNav__list}>
                        {hierarchicalMenu.map((item, i) => {
                          const hasChildren = item.children && item.children.length > 0
                          const isActiveHub = linkIsActive(item, "main")
                          if (hasChildren) {
                            return (
                              <li key={`m-parent-${i}`}>
                                <button
                                  type="button"
                                  ref={el => (mobileParentButtonRefs.current[item.title] = el)}
                                  className={cx(s.Nav__link, s.MobileNav__row, {
                                    [s.MobileNav__row___activeHub]: isActiveHub,
                                  })}
                                  aria-haspopup="true"
                                  aria-expanded={false}
                                  tabIndex={submenuOpen ? -1 : 0}
                                  onClick={() => {
                                    lastOpenedParentTitleRef.current = item.title
                                    setMobileSubmenuParent(item)
                                  }}
                                >
                                  <span>{item.title}</span>
                                  <Arrow className={s.MobileNav__chevron} />
                                </button>
                              </li>
                            )
                          }
                          return (
                            <li key={`m-parent-${i}`}>
                              <Link
                                to={parseLink(item.url)}
                                className={cx(s.Nav__link, s.MobileNav__row, {
                                  [s.MobileNav__row___activeHub]: isActiveHub,
                                })}
                                tabIndex={submenuOpen ? -1 : 0}
                                onClick={() => setShowMenu(false)}
                              >
                                <span>{item.title}</span>
                                <Arrow className={s.MobileNav__chevron} />
                              </Link>
                            </li>
                          )
                        })}
                      </ul>
                    </div>

                    <div
                      className={s.MobileNav__panel}
                      aria-hidden={!submenuOpen}
                    >
                      {mobileSubmenuParent && (
                        <>
                          <button
                            type="button"
                            ref={mobileBackButtonRef}
                            className={s.MobileNav__back}
                            tabIndex={submenuOpen ? 0 : -1}
                            onClick={() => setMobileSubmenuParent(null)}
                          >
                            <Arrow className={s.MobileNav__backArrow} />
                            <span>Back</span>
                          </button>
                          <Link
                            to={parseLink(mobileSubmenuParent.url)}
                            className={cx(s.Nav__link, s.MobileNav__row, s.MobileNav__parentHeading, {
                              [s.MobileNav__row___activeHub]: isCurrentChildPage(mobileSubmenuParent),
                            })}
                            tabIndex={submenuOpen ? 0 : -1}
                            onClick={() => setShowMenu(false)}
                          >
                            <span>{mobileSubmenuParent.title}</span>
                            <Arrow className={s.MobileNav__chevron} />
                          </Link>
                          <ul className={s.MobileNav__list}>
                            {mobileSubmenuParent.children?.map((child, i) => (
                              <li key={`m-child-${i}`}>
                                <Link
                                  to={parseLink(child.url)}
                                  className={cx(s.Nav__link, s.MobileNav__row, {
                                    [s.MobileNav__row___activeHub]: isCurrentChildPage(child),
                                  })}
                                  tabIndex={submenuOpen ? 0 : -1}
                                  onClick={() => setShowMenu(false)}
                                >
                                  <span>{child.title}</span>
                                  <Arrow className={s.MobileNav__chevron} />
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className={s.NavMain__countryPicker}>
                  <span>{countryPicker.countryPickerTitle}</span>
                  <div className={s.NavMain__countryPicker__container}>
                    {countryPicker.countryPickerOptions.map(country => (
                      <a href={country.url} key={country.title}>
                        <div>
                          <img
                            width="150"
                            height="150"
                            src={country.flagImage.sourceUrl}
                            alt={country.flagImage.altText}
                          />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={s.MenuWrapper__menuCtas}>
          {wpPage.acfHome.menuCtas.map((cta, i) => {
            return <MenuCta {...cta} key={i} setShowMenu={setShowMenu} />
          })}
        </div>
      </div>
      <MenuFooter />
    </>
  )
}

const MenuCta = data => {
  return (
      <div 
        className={cx(s.MenuCta, {
          [s.MenuCta___gradient]: data.image,
        })}
        onClick={() => {
          data.setShowMenu(false)
        }}
        style={
          data.image
            ? {
                backgroundImage: `url(${getSrc(data.image.localFile)})`,
              }
            : data.color
              ? {
                  backgroundColor: data.color,
                }
              : {}
        }
      >
        <Arrow className={s.MenuCta__arrow} />
        <span className={s.MenuCta__subTitle}>{data.subTitle}</span>
        <span className={s.MenuCta__title}>
          <Link to={parseLink(data.link.url)}>
            {data.title}
          </Link>
        </span>
      </div>
  )
}

const SubMenu = ({
  title,
  links,
  activeHub,
  setShowMenu,
  setActiveHub,
  linkIsActive,
}) => {
  const isActive = activeHub === title

  return (
    <div
      className={cx(
        s.NavSub,
        s.NavSub___hasActive,
        isActive ? s.NavSub___animate : s.NavSub___noAnimate
      )}
      aria-hidden={!isActive}
    >
      <span className={s.Nav__subTitle}>{title}</span>
      <ul>
        {links?.map((link, i) => (
          <li
            key={i}
            onClick={() => {
              setShowMenu(false)
            }}
          >
            <Link
              to={parseLink(link.url)}
              tabIndex={isActive ? 0 : -1}
              className={cx(s.Nav__link, {
                [s.Nav__link___active]: linkIsActive(link, "sub"),
              })}
            >
              {link.title}
              <Arrow />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
